import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  noindex = false,
  structuredData,
}) => {
  const siteUrl = 'https://fitnession.fit';
  const defaultTitle = 'Fitnession - Personalized Nutrition, Workouts & Coaching';
  const defaultDescription = 'Transform your health with Fitnession. Personalized meal plans, custom workout programs, and expert 1:1 coaching for sustainable weight loss. Medical support including GLP-1 available for eligible members.';
  const defaultImage = `${siteUrl}/og-image.jpg`;
  const defaultKeywords = 'weight loss program, personalized nutrition, meal plans, fitness coaching, workout programs, 1:1 coaching, BMI calculator, TDEE calculator, macro calculator, sustainable weight loss, lifestyle coaching, GLP-1 medication, weight management';

  const seo = {
    title: title ? `${title} | Fitnession` : defaultTitle,
    description: description || defaultDescription,
    image: image || defaultImage,
    url: url ? `${siteUrl}${url}` : siteUrl,
    keywords: keywords || defaultKeywords,
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <link rel="canonical" href={seo.url} />

      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Fitnession" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-configured SEO for common pages
export const PageSEO = {
  home: {
    title: null, // Will use default
    description: 'Transform your health with Fitnession. Personalized meal plans, custom workouts, and expert 1:1 coaching for sustainable weight loss. GLP-1 support available for eligible members.',
    url: '/',
  },
  calculators: {
    title: 'Free Fitness Calculators',
    description: 'Use our free fitness calculators - BMI, TDEE, Macro, Ideal Weight, Water Intake, and Weight Loss Timeline calculators to plan your health journey.',
    url: '/calculators',
    keywords: 'BMI calculator, TDEE calculator, calorie calculator, macro calculator, ideal weight calculator, water intake calculator, weight loss calculator',
  },
  bmiCalculator: {
    title: 'BMI Calculator - Check Your Body Mass Index',
    description: 'Calculate your BMI (Body Mass Index) instantly with our free calculator. See where you stand on the health scale and get personalized recommendations.',
    url: '/calculators/bmi',
    keywords: 'BMI calculator, body mass index, BMI chart, healthy BMI, calculate BMI, BMI scale',
  },
  tdeeCalculator: {
    title: 'TDEE Calculator - Daily Calorie Needs',
    description: 'Calculate your Total Daily Energy Expenditure (TDEE) and daily calorie needs. Find out how many calories you need to lose, maintain, or gain weight.',
    url: '/calculators/tdee',
    keywords: 'TDEE calculator, calorie calculator, daily calorie needs, BMR calculator, calorie deficit, maintenance calories',
  },
  macroCalculator: {
    title: 'Macro Calculator - Protein, Carbs & Fat',
    description: 'Get your personalized macronutrient targets. Calculate the ideal protein, carbohydrate, and fat intake for your fitness goals.',
    url: '/calculators/macro',
    keywords: 'macro calculator, macronutrient calculator, protein calculator, carb calculator, fat intake, IIFYM calculator',
  },
  weightLossTimeline: {
    title: 'Weight Loss Timeline Calculator',
    description: 'Find out how long it will take to reach your goal weight. Get a realistic timeline with milestones for your weight loss journey.',
    url: '/calculators/weight-loss-timeline',
    keywords: 'weight loss timeline, weight loss calculator, how long to lose weight, weight loss goals, weight loss milestones',
  },
  idealWeight: {
    title: 'Ideal Body Weight Calculator',
    description: 'Calculate your ideal body weight using multiple scientific formulas. Find your healthy weight range based on height, gender, and body frame.',
    url: '/calculators/ideal-weight',
    keywords: 'ideal body weight, ideal weight calculator, healthy weight, IBW calculator, Devine formula, Robinson formula',
  },
  waterIntake: {
    title: 'Water Intake Calculator - Daily Hydration',
    description: 'Calculate how much water you should drink daily. Get a personalized hydration schedule based on your weight, activity level, and climate.',
    url: '/calculators/water-intake',
    keywords: 'water intake calculator, daily water needs, hydration calculator, how much water to drink, water consumption',
  },
  assessment: {
    title: 'Free Fitness Assessment',
    description: 'Take our free 2-minute assessment to get your personalized program recommendation. Check eligibility for lifestyle coaching and GLP-1 programs.',
    url: '/assessment',
    keywords: 'fitness assessment, weight loss quiz, personalized program, free health assessment, GLP-1 eligibility',
  },
  pricing: {
    title: 'Pricing - Fitness & Weight Loss Programs',
    description: 'Explore Fitnession pricing plans. Choose from Core, Medical, or GLP-1 programs. Personalized coaching starting at affordable prices.',
    url: '/pricing',
    keywords: 'fitness program price, coaching pricing, weight loss subscription, meal plan pricing, workout program cost',
  },
  blog: {
    title: 'Blog - Fitness Tips & Health Insights',
    description: 'Expert articles on weight loss, nutrition, fitness tips, GLP-1 medications, and healthy lifestyle. Stay informed on your health journey.',
    url: '/blog',
    keywords: 'fitness blog, health tips, nutrition articles, workout advice, GLP-1 information, healthy lifestyle',
  },
  about: {
    title: 'About Us',
    description: 'Learn about Fitnession and our mission to help people achieve sustainable weight loss through science-based programs and personalized support.',
    url: '/about',
  },
  faq: {
    title: 'Frequently Asked Questions',
    description: 'Find answers to common questions about Fitnession programs, coaching, nutrition plans, GLP-1 medications, and pricing.',
    url: '/faq',
    keywords: 'fitness FAQ, weight loss questions, program FAQ, GLP-1 questions, coaching help',
  },
  login: {
    title: 'Sign In',
    description: 'Sign in to your Fitnession account to access your dashboard, track progress, and connect with your coach.',
    url: '/login',
    noindex: true,
  },
  register: {
    title: 'Create Account',
    description: 'Join Fitnession today. Create your free account to start your personalized weight loss journey.',
    url: '/register',
  },
  ourProgram: {
    title: 'Our Fitness & Weight Loss Program',
    description: 'Discover our comprehensive program combining personalized coaching, nutrition plans, and lifestyle support. GLP-1 medication available for eligible members.',
    url: '/our-program',
    keywords: 'weight loss program, personalized coaching, nutrition plan, fitness program, lifestyle transformation, GLP-1 support',
  },
  successStories: {
    title: 'Success Stories - Real Transformations',
    description: 'Read inspiring weight loss success stories from real Fitnession members. See their transformations and learn how they achieved their health goals.',
    url: '/success-stories',
    keywords: 'weight loss success stories, transformation stories, before and after, weight loss results, customer testimonials',
  },
  forgotPassword: {
    title: 'Forgot Password',
    description: 'Reset your Fitnession account password. We will send you a link to create a new password.',
    url: '/forgot-password',
    noindex: true,
  },
  resetPassword: {
    title: 'Reset Password',
    description: 'Create a new password for your Fitnession account.',
    url: '/reset-password',
    noindex: true,
  },
};

// Structured Data Templates
export const StructuredData = {
  organization: {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Fitnession',
    url: 'https://fitnession.fit',
    logo: 'https://fitnession.fit/logo512.png',
    description: 'Personalized nutrition, custom workout programs, and expert 1:1 coaching for sustainable weight loss. GLP-1 medication support available.',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@fitnession.fit',
    },
    sameAs: [
      // Add social media URLs when available
    ],
  },

  website: {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Fitnession',
    url: 'https://fitnession.fit',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://fitnession.fit/blog?search={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  },

  calculator: (name, description, url) => ({
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: name,
    description: description,
    url: `https://fitnession.fit${url}`,
    applicationCategory: 'HealthApplication',
    operatingSystem: 'Any',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'INR',
    },
  }),

  faqPage: (faqs) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }),

  blogPost: (title, description, image, datePublished, author) => ({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description: description,
    image: image,
    datePublished: datePublished,
    author: {
      '@type': 'Person',
      name: author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Fitnession',
      logo: {
        '@type': 'ImageObject',
        url: 'https://fitnession.fit/logo512.png',
      },
    },
  }),
};

export default SEO;
